import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertCircle, CheckCircle2, Loader2, RefreshCw } from 'lucide-react';

import { createClientRequestId } from '../../utils/clientRequestId';
import { riderApi } from '../services/riderApi';

function money(cents) {
  return new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP' })
    .format(Number(cents || 0) / 100);
}

function blockerCopy(blocker) {
  if (blocker.code === 'CASH_IN_RIDER_CUSTODY') {
    return `${money(blocker.amountCents)} from Parcel ${blocker.shipmentId} still needs to be handed to the business and confirmed.`;
  }
  if (blocker.code === 'PARCEL_IN_RIDER_CUSTODY') {
    return `Parcel ${blocker.shipmentId} is still in your custody. Present it at the returns counter or wait for an authorized loss decision.`;
  }
  if (blocker.code === 'DISPOSITION_PENDING') {
    return `Failed stop ${blocker.stopId} is waiting for the business to decide what happens next.`;
  }
  if (blocker.code === 'STOP_UNRESOLVED') {
    return `Stop ${blocker.stopId} is not finished yet. Complete its current delivery step first.`;
  }
  return 'This trip still has an open delivery responsibility.';
}

function completionError(error) {
  if (['RUN_COMPLETION_VERSION_CONFLICT', 'RUN_COMPLETION_NOT_ACTIVE'].includes(error?.code)) {
    return 'This trip changed while you were finishing it. We refreshed the latest state; review it before trying again.';
  }
  if (error?.code === 'RUN_COMPLETION_OBLIGATIONS_OPEN') {
    return 'This trip still has open Parcel or cash responsibilities. The latest blockers are shown below.';
  }
  if (error?.code === 'RUN_COMPLETION_ALREADY_COMPLETE') {
    return 'This trip was already completed from another request. Refresh your active work and open History.';
  }
  if (error?.status === 403) return 'You are not authorized to finish this trip.';
  if (error?.status === 404) return 'This trip is not assigned to you or is no longer available.';
  return 'We could not finish this trip. Check your connection and try again.';
}

export default function RiderRunCompletionCard({
  businessId,
  run,
  executionEnabled,
  executionHeaders,
  onCompleted,
  onRefresh,
}) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const idempotencyKey = useRef(createClientRequestId(`run_complete_${run.id}`));

  const load = useCallback(async ({ quiet = false, preserveError = false, signal } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const result = await riderApi.previewRunCompletion(businessId, run.id, {
        executionHeaders, signal,
      });
      if (signal?.aborted) return;
      setPreview(result);
      if (!preserveError) setError('');
    } catch (requestError) {
      if (signal?.aborted || requestError?.code === 'ERR_CANCELED') return;
      setError(completionError(requestError));
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [businessId, executionHeaders, run.id]);

  useEffect(() => {
    const controller = new AbortController();
    load({ signal: controller.signal });
    return () => controller.abort();
  }, [load, run.version]);

  const complete = async () => {
    if (!preview?.completable || !executionEnabled || busy) return;
    setBusy(true);
    setError('');
    try {
      const result = await riderApi.completeRun(businessId, run.id, {
        expectedRunVersion: run.version,
        idempotencyKey: idempotencyKey.current,
      }, { executionHeaders });
      idempotencyKey.current = createClientRequestId(`run_complete_${run.id}`);
      await onCompleted?.(result);
    } catch (requestError) {
      setError(completionError(requestError));
      if (['RUN_COMPLETION_VERSION_CONFLICT', 'RUN_COMPLETION_NOT_ACTIVE', 'RUN_COMPLETION_OBLIGATIONS_OPEN', 'RUN_COMPLETION_ALREADY_COMPLETE'].includes(requestError?.code)) {
        await onRefresh?.();
        await load({ quiet: true, preserveError: true });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <section aria-labelledby="run-completion-heading" className="rounded-3xl border border-emerald-200 bg-emerald-50/50 p-5 dark:border-emerald-500/20 dark:bg-emerald-500/5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-emerald-700 dark:text-emerald-300">Trip finish</p>
          <h2 id="run-completion-heading" className="mt-1 text-lg font-semibold">Complete this trip</h2>
          <p className="mt-1 max-w-xl text-sm text-stone-600 dark:text-stone-300">The system checks every Parcel and cash handover before this trip can move to History.</p>
        </div>
        <button type="button" onClick={() => load()} disabled={loading || busy} aria-label="Refresh completion check" className="grid h-11 w-11 place-items-center rounded-xl border border-stone-300 disabled:opacity-50 dark:border-white/15">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
        </button>
      </div>

      {error && <p role="alert" className="mt-4 flex gap-2 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-200"><AlertCircle className="h-5 w-5 shrink-0" aria-hidden="true" />{error}</p>}

      {loading && !preview ? (
        <div role="status" className="mt-4 flex min-h-20 items-center gap-3 text-sm text-stone-500"><Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> Checking Parcel and cash responsibilities…</div>
      ) : preview?.completable ? (
        <div className="mt-4 rounded-2xl border border-emerald-200 bg-white p-4 dark:border-emerald-500/20 dark:bg-neutral-900">
          <div className="flex gap-3"><CheckCircle2 className="h-6 w-6 shrink-0 text-emerald-600" aria-hidden="true" /><div><h3 className="font-semibold">Everything is accounted for</h3><p className="mt-1 text-sm text-stone-600 dark:text-stone-300">Finishing moves this trip out of active work and into History. The server rechecks the latest state before saving.</p></div></div>
          {!executionEnabled && <p className="mt-3 text-sm font-medium text-amber-800 dark:text-amber-200">Claim control of this trip on this device before finishing it.</p>}
          <button type="button" onClick={complete} disabled={busy || !executionEnabled} className="mt-4 inline-flex min-h-12 items-center gap-2 rounded-2xl bg-emerald-700 px-5 font-semibold text-white disabled:opacity-50 dark:bg-emerald-500 dark:text-stone-950">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> : <CheckCircle2 className="h-5 w-5" aria-hidden="true" />}
            Finish trip and open History
          </button>
        </div>
      ) : preview ? (
        <div className="mt-4">
          <h3 className="font-semibold">Resolve these before finishing</h3>
          <ul className="mt-3 space-y-2">
            {(preview.blockers || []).map((blocker, index) => (
              <li key={`${blocker.code}:${blocker.stopId || blocker.codRecordId || index}`} className="flex gap-3 rounded-2xl bg-white p-3 text-sm dark:bg-neutral-900">
                <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" />
                <span>{blockerCopy(blocker)}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}
